import { execSync } from 'child_process'
import { readFileSync } from 'fs'
import { sep } from 'path'

/**
 * A single exported symbol from a TypeScript source file.
 * Used by step-7 reconcile Y2 interface diff (baseline SHA vs HEAD).
 * See ADR-0022.
 */
export interface ExportSymbol {
  name: string
  /** 'function' | 'class' | 'interface' | 'type' | 'enum' | 'const' | 'other' */
  kind: string
}

/**
 * Parse exports from TypeScript source content. Pure function — no file system access.
 *
 * Caller is responsible for providing the source string. For live files, use
 * readFileSync; for historical content, use parseExportsFromGitRevision which
 * shells out to `git show <sha>:<file>`.
 */
export async function parseExports(source: string, _fileName: string): Promise<ExportSymbol[]> {
  const { Project } = await import('ts-morph')
  const project = new Project({
    compilerOptions: {
      allowJs: false,
      declaration: false,
      skipLibCheck: true,
      noEmit: true,
    },
    useInMemoryFileSystem: true,
  })

  // Memory FS path must end in .ts for ts-morph to parse as TS
  const memPath = '/in-memory.ts'
  const sourceFile = project.createSourceFile(memPath, source, { overwrite: true })

    const symbols: ExportSymbol[] = []
  for (const sym of sourceFile.getExportSymbols() as Array<{ getName(): string; getDeclarations(): Array<{ getKindName(): string | (() => string) }> }>) {
    symbols.push({
      name: sym.getName(),
      kind: classifySymbolKind(sym),
    })
  }
  return symbols
}

/**
 * Parse exports from a list of files at a specific git revision.
 *
 * For each file: `git show <sha>:<file>` to fetch historical content, then parseExports.
 * Deleted files (file exists at HEAD but not at <sha>) are skipped with the returned map
 * reflecting absence — caller can diff against HEAD exports to compute "added".
 *
 * Returns map keyed by repo-relative file path (forward slashes).
 */
export async function parseExportsFromGitRevision(
  projectRoot: string,
  sha: string,
  files: string[],
): Promise<Map<string, ExportSymbol[]>> {
  const result = new Map<string, ExportSymbol[]>()

  for (const file of files) {
    let content: string
    try {
      content = execSync(`git show ${sha}:${file}`, {
        cwd: projectRoot,
        stdio: ['ignore', 'pipe', 'pipe'],
        timeout: 5000,
        maxBuffer: 5 * 1024 * 1024,
      }).toString('utf-8')
    } catch {
      // File didn't exist at this revision (newly added at HEAD) — skip, will show as added
      continue
    }

    if (!content.trim()) continue
    const rel = normalizePath(file)
    const symbols = await parseExports(content, rel)
    result.set(rel, symbols)
  }

  return result
}

/**
 * Read current exports from the working tree for a list of files.
 */
export async function parseExportsFromWorkingTree(
  projectRoot: string,
  files: string[],
): Promise<Map<string, ExportSymbol[]>> {
  const result = new Map<string, ExportSymbol[]>()

  for (const file of files) {
    const rel = normalizePath(file)
    let content: string
    try {
      content = readFileSync(`${projectRoot}${sep}${rel}`, 'utf-8')
    } catch {
      continue
    }
    if (!content.trim()) continue
    const symbols = await parseExports(content, rel)
    result.set(rel, symbols)
  }

  return result
}

/**
 * Resolve the git revision at baseline freeze time, per ADR-0022.
 *
 * 1. If baseline.frozen_at_sha is present, use it directly.
 * 2. Otherwise fall back to `git rev-list --before=<frozen_at> -1` and warn.
 *
 * Returns { sha, source } where source is 'baseline-field' | 'timestamp-fallback' | 'none'.
 */
export function resolveBaselineSha(
  projectRoot: string,
  frozenAtSha: string | undefined,
  frozenAt: string,
): { sha: string | null; source: 'baseline-field' | 'timestamp-fallback' | 'none'; warning?: string } {
  if (frozenAtSha && /^[0-9a-f]{7,40}$/.test(frozenAtSha)) {
    return { sha: frozenAtSha, source: 'baseline-field' }
  }

  if (!frozenAt) {
    return {
      sha: null,
      source: 'none',
      warning: 'baseline has no frozen_at_sha and no frozen_at — interface diff disabled',
    }
  }

  try {
    const sha = execSync(`git rev-list -1 --before=${frozenAt} --format=%H HEAD`, {
      cwd: projectRoot,
      stdio: ['ignore', 'pipe', 'pipe'],
      timeout: 5000,
    }).toString('utf-8').trim().split('\n').filter(l => /^[0-9a-f]{40}$/.test(l))[0]

    if (!sha) {
      return {
        sha: null,
        source: 'none',
        warning: `git rev-list --before=${frozenAt} returned no commit — interface diff disabled`,
      }
    }
    return {
      sha,
      source: 'timestamp-fallback',
      warning: `baseline.frozen_at_sha missing — using timestamp-fallback ${sha.slice(0, 8)} (precision may be off if same-second commits exist)`,
    }
  } catch (err: any) {
    return {
      sha: null,
      source: 'none',
      warning: `git rev-list failed: ${err.message?.split('\n')[0] ?? err.message}`,
    }
  }
}

/**
 * Diff two snapshots of per-file exports. Returns per-file added/removed.
 * Signature change detection is left to future work (requires AST compare).
 */
export function diffExportSnapshots(
  before: Map<string, ExportSymbol[]>,
  after: Map<string, ExportSymbol[]>,
): Array<{
  file: string
  added: ExportSymbol[]
  removed: ExportSymbol[]
  changed: ExportSymbol[]
}> {
  const allFiles = new Set<string>([...before.keys(), ...after.keys()])
  const result: Array<{ file: string; added: ExportSymbol[]; removed: ExportSymbol[]; changed: ExportSymbol[] }> = []

  for (const file of allFiles) {
    const beforeSyms = before.get(file) ?? []
    const afterSyms = after.get(file) ?? []
    const beforeMap = new Map(beforeSyms.map(s => [s.name, s]))
    const afterMap = new Map(afterSyms.map(s => [s.name, s]))

    const added = afterSyms.filter(s => !beforeMap.has(s.name))
    const removed = beforeSyms.filter(s => !afterMap.has(s.name))
    const changed = afterSyms.filter(s => {
      const old = beforeMap.get(s.name)
      return old && old.kind !== s.kind
    })

    if (added.length || removed.length || changed.length) {
      result.push({ file, added, removed, changed })
    }
  }

  return result.sort((a, b) => a.file.localeCompare(b.file))
}

function normalizePath(p: string): string {
  return p.replace(/\\/g, '/')
}

function classifySymbolKind(sym: { getDeclarations(): Array<{ getKindName(): string | (() => string) }> }): string {
  try {
    const decls = sym.getDeclarations()
    if (!decls || decls.length === 0) return 'other'
    const raw = decls[0].getKindName()
    const kindName = typeof raw === 'function' ? (raw as () => string)() : raw
    const lower = kindName.toLowerCase()
    if (lower.includes('function')) return 'function'
    if (lower.includes('class')) return 'class'
    if (lower.includes('interface')) return 'interface'
    if (lower.includes('typealias') || lower.includes('type')) return 'type'
    if (lower.includes('enum')) return 'enum'
    if (lower.includes('variable') || lower.includes('const')) return 'const'
    return kindName || 'other'
  } catch {
    return 'other'
  }
}
